import {
  cronToPreset,
  isValidTime,
  isValidWindow,
  normalizeCron,
  presetToCron,
  validateCron,
  type CronFieldName,
  type HourStep,
  type HourWindow,
  type MinuteStep,
  type SchedulePreset,
  type SchedulePresetKind,
} from "@prodgy/schedule";

import { DEFAULT_TIME } from "./schedule";

/**
 * O estado do seletor de frequência (AB#87330). Guarda o campo de cada tipo ao
 * mesmo tempo — trocar de "semanal" para "mensal" e voltar não perde os dias
 * marcados —, e só o tipo escolhido vira cron.
 */
export interface FrequencyDraft {
  kind: SchedulePresetKind;
  minutes: MinuteStep;
  hours: HourStep;
  windowOn: boolean;
  window: HourWindow;
  weekdaysOnly: boolean;
  days: number[];
  monthDay: number | "last";
  time: string;
  /** O texto do campo avançado, como a pessoa escreveu. */
  cron: string;
}

export type DraftProblem =
  | { key: "invalidTime" | "invalidWindow" | "pickDays" | "invalidCron" }
  | { key: "invalidCronField"; field: CronFieldName };

const DEFAULTS: FrequencyDraft = {
  kind: "weekdays",
  minutes: 15,
  hours: 1,
  windowOn: false,
  window: { from: 6, to: 18 },
  weekdaysOnly: false,
  days: [1],
  monthDay: 1,
  time: DEFAULT_TIME,
  cron: "0 9 * * 1-5",
};

/** O seletor aberto num cron: o preset dele, ou o avançado quando não há preset. */
export function draftFromCron(cron: string, base: FrequencyDraft = DEFAULTS): FrequencyDraft {
  const preset = cronToPreset(cron);
  const draft: FrequencyDraft = { ...base, kind: preset.kind, cron: normalizeCron(cron) || base.cron };
  switch (preset.kind) {
    case "everyMinutes":
      return { ...draft, minutes: preset.minutes, ...windowOf(preset.window, base), weekdaysOnly: preset.weekdaysOnly };
    case "everyHours":
      return { ...draft, hours: preset.hours, ...windowOf(preset.window, base), weekdaysOnly: preset.weekdaysOnly };
    case "daily":
    case "weekdays":
      return { ...draft, time: preset.time };
    case "weekly":
      return { ...draft, days: preset.days, time: preset.time };
    case "monthly":
      return { ...draft, monthDay: preset.day, time: preset.time };
    default:
      return draft;
  }
}

/**
 * O texto do campo avançado mudou. Se ele equivale a um preset, o seletor troca
 * para o preset; senão fica no avançado com o texto como está.
 */
export function withCustomCron(draft: FrequencyDraft, text: string): FrequencyDraft {
  if (cronToPreset(text).kind !== "custom") return draftFromCron(text, draft);
  return { ...draft, kind: "custom", cron: text };
}

export function draftToPreset(draft: FrequencyDraft): SchedulePreset {
  const window = draft.windowOn ? draft.window : null;
  switch (draft.kind) {
    case "everyMinutes":
      return { kind: "everyMinutes", minutes: draft.minutes, window, weekdaysOnly: draft.weekdaysOnly };
    case "everyHours":
      return { kind: "everyHours", hours: draft.hours, window, weekdaysOnly: draft.weekdaysOnly };
    case "daily":
    case "weekdays":
      return { kind: draft.kind, time: draft.time };
    case "weekly":
      return { kind: "weekly", days: draft.days, time: draft.time };
    case "monthly":
      return { kind: "monthly", day: draft.monthDay, time: draft.time };
    default:
      return { kind: "custom", cron: draft.cron };
  }
}

/** O cron que o rascunho grava. */
export function draftCron(draft: FrequencyDraft): string {
  return presetToCron(draftToPreset(draft));
}

/** O que impede salvar, ou null. */
export function draftProblem(draft: FrequencyDraft): DraftProblem | null {
  switch (draft.kind) {
    case "everyMinutes":
    case "everyHours":
      return draft.windowOn && !isValidWindow(draft.window) ? { key: "invalidWindow" } : null;
    case "weekly":
      if (draft.days.length === 0) return { key: "pickDays" };
      return isValidTime(draft.time) ? null : { key: "invalidTime" };
    case "custom": {
      const result = validateCron(draft.cron);
      if (result.ok) return null;
      return result.field ? { key: "invalidCronField", field: result.field } : { key: "invalidCron" };
    }
    default:
      return isValidTime(draft.time) ? null : { key: "invalidTime" };
  }
}

function windowOf(window: HourWindow | null, base: FrequencyDraft): Pick<FrequencyDraft, "windowOn" | "window"> {
  return window ? { windowOn: true, window } : { windowOn: false, window: base.window };
}
